import Vue from 'vue';
import types from '../../mutation-types';
import getters, { getSelectedChatConversation } from './getters';
import actions from './actions';
import { findPendingMessageIndex, SORT_AFFECTING_FIELDS } from './helpers';
import { MESSAGE_STATUS } from 'shared/constants/messages';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from '../../../../shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';

const state = {
  allConversations: [],
  listLoadingStatus: true,
  isFetchingConversation: false,
  chatStatusFilter: wootConstants.STATUS_TYPE.OPEN,
  chatSortFilter: wootConstants.SORT_BY_TYPE.LATEST,
  currentInbox: null,
  selectedChatId: null,
  appliedFilters: [],
  contextMenuChatId: null,
  conversationParticipants: [],
  conversationLastSeen: null,
  syncConversationsMessages: {},
  conversationFilters: {},
  takeOverConversationIds: [],
  // Version counter for cache invalidation - incremented when conversations change
  conversationsVersion: 0,
};

// Helper to check if an update affects sorting
const shouldInvalidateSort = (prev, next) =>
  SORT_AFFECTING_FIELDS.some(
    field => next[field] !== undefined && next[field] !== prev[field]
  );

// Helper to check if an update affects filtering (assignee/team changes)
// These don't affect sort order but DO affect which tab conversations appear in
const shouldInvalidateFilters = (prev, next) => {
  // Check if assignee changed (including null transitions)
  const assigneeChanged = (prev.meta?.assignee?.id ?? null) !== (next.meta?.assignee?.id ?? null);
  // Check if team ID changed
  const teamIdChanged = (prev.meta?.team?.id ?? null) !== (next.meta?.team?.id ?? null);
  // Check if team membership changed (critical for tab visibility)
  // Normalize undefined/null to avoid noisy invalidations from inconsistent backend payloads
  const teamMemberChanged =
    (prev.meta?.team?.is_member ?? null) !== (next.meta?.team?.is_member ?? null);

  return assigneeChanged || teamIdChanged || teamMemberChanged;
};

// Helper to invalidate conversation cache when data changes
// Increments version counter which triggers re-computation of cached getters
// Used for both sort-affecting changes AND filter-affecting changes (assignee/team/membership)
const invalidateConversationCache = _state => {
  _state.conversationsVersion += 1;
};


// mutations
export const mutations = {
  [types.SET_CONVERSATION_UI_FLAG](state, data) {
    state.uiFlags = {
      ...state.uiFlags,
      ...data
    };
  },
  [types.SET_ALL_CONVERSATION](_state, conversationList) {
    const newAllConversations = [..._state.allConversations];
    conversationList.forEach(conversation => {
      const indexInCurrentList = newAllConversations.findIndex(
        c => c.id === conversation.id
      );
      if (indexInCurrentList < 0) {
        newAllConversations.push(conversation);
      } else if (conversation.id !== _state.selectedChatId) {
        // If the conversation is already in the list, replace it
        // Added this to fix the issue of the conversation not being updated
        // When reconnecting to the websocket. If the selectedChatId is not the same as
        // the conversation.id in the store, replace the existing conversation with the new one
        newAllConversations[indexInCurrentList] = conversation;
      } else {
        // If the conversation is already in the list and selectedChatId is the same,
        // replace all data except the messages array, attachments, dataFetched, allMessagesLoaded
        const existingConversation = newAllConversations[indexInCurrentList];
        newAllConversations[indexInCurrentList] = {
          ...conversation,
          allMessagesLoaded: existingConversation.allMessagesLoaded,
          messages: existingConversation.messages,
          dataFetched: existingConversation.dataFetched,
          attachments: existingConversation.attachments,
        };
      }
    });
    _state.allConversations = newAllConversations;
    invalidateConversationCache(_state);
  },
  [types.EMPTY_ALL_CONVERSATION](_state) {
    _state.allConversations = [];
    _state.selectedChatId = null;
    invalidateConversationCache(_state);
  },
  [types.SET_ALL_MESSAGES_LOADED](_state) {
    const [chat] = getSelectedChatConversation(_state);
    Vue.set(chat, 'allMessagesLoaded', true);
  },

  [types.CLEAR_ALL_MESSAGES_LOADED](_state) {
    const [chat] = getSelectedChatConversation(_state);
    Vue.set(chat, 'allMessagesLoaded', false);
  },
  [types.CLEAR_CURRENT_CHAT_WINDOW](_state) {
    _state.selectedChatId = null;
  },

  [types.SET_PREVIOUS_CONVERSATIONS](_state, { id, data }) {
    if (data.length) {
      const [chat] = _state.allConversations.filter(c => c.id === id);
      chat.messages.unshift(...data);
    }
  },
  [types.SET_ALL_ATTACHMENTS](_state, { id, data }) {
    const [chat] = _state.allConversations.filter(c => c.id === id);
    if (!chat) return;
    Vue.set(chat, 'attachments', []);
    chat.attachments.push(...data);
  },
  [types.SET_MISSING_MESSAGES](_state, { id, data }) {
    const [chat] = _state.allConversations.filter(c => c.id === id);
    if (!chat) return;
    Vue.set(chat, 'messages', data);
  },

  [types.SET_CURRENT_CHAT_WINDOW](_state, activeChat) {
    if (activeChat) {
      _state.selectedChatId = activeChat.id;
    }
  },

  [types.ASSIGN_AGENT](_state, assignee) {
    const [chat] = getSelectedChatConversation(_state);
    if (!chat) return; // Guard: no selected conversation
    Vue.set(chat.meta, 'assignee', assignee);
    // Assignee change affects filtering (which tab conversation appears in)
    invalidateConversationCache(_state);
  },

  [types.ASSIGN_TEAM](_state, { team, conversationId }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (!chat) return; // Guard: conversation not found
    Vue.set(chat.meta, 'team', team);
    // Team change affects filtering (which tab conversation appears in)
    invalidateConversationCache(_state);
  },

  [types.UPDATE_CONVERSATION_LAST_ACTIVITY](
    _state,
    { lastActivityAt, conversationId }
  ) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (chat) {
      Vue.set(chat, 'last_activity_at', lastActivityAt);
      // Invalidate cache since last_activity_at affects sorting
      invalidateConversationCache(_state);
    }
  },
  [types.ASSIGN_PRIORITY](_state, { priority, conversationId }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (chat && chat.priority !== priority) {
      Vue.set(chat, 'priority', priority);
      invalidateConversationCache(_state); // priority affects sorting
    }
  },

  [types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES](_state, custom_attributes) {
    const [chat] = getSelectedChatConversation(_state);
    Vue.set(chat, 'custom_attributes', custom_attributes);
  },

  [types.CHANGE_CONVERSATION_STATUS](
    _state,
    { conversationId, status, snoozedUntil }
  ) {
    const conversation =
      getters.getConversationById(_state)(conversationId) || {};
    Vue.set(conversation, 'snoozed_until', snoozedUntil);
    Vue.set(conversation, 'status', status);
  },

  [types.MUTE_CONVERSATION](_state) {
    const [chat] = getSelectedChatConversation(_state);
    Vue.set(chat, 'muted', true);
  },

  [types.UNMUTE_CONVERSATION](_state) {
    const [chat] = getSelectedChatConversation(_state);
    Vue.set(chat, 'muted', false);
  },

  [types.ADD_CONVERSATION_ATTACHMENTS]({ allConversations }, message) {
    const { conversation_id: conversationId } = message;
    const [chat] = getSelectedChatConversation({
      allConversations,
      selectedChatId: conversationId,
    });

    if (!chat) return;

    const isMessageSent =
      message.status === MESSAGE_STATUS.SENT && message.attachments;
    if (isMessageSent) {
      message.attachments.forEach(attachment => {
        if (!chat.attachments.some(a => a.id === attachment.id)) {
          chat.attachments.push(attachment);
        }
      });
    }
  },

  [types.DELETE_CONVERSATION_ATTACHMENTS]({ allConversations }, message) {
    const { conversation_id: conversationId } = message;
    const [chat] = getSelectedChatConversation({
      allConversations,
      selectedChatId: conversationId,
    });

    if (!chat) return;

    const isMessageSent = message.status === MESSAGE_STATUS.SENT;
    if (isMessageSent) {
      const attachmentIndex = chat.attachments.findIndex(
        a => a.message_id === message.id
      );
      if (attachmentIndex !== -1) chat.attachments.splice(attachmentIndex, 1);
    }
  },

  [types.ADD_MESSAGE]({ allConversations, selectedChatId }, message) {
    const { conversation_id: conversationId } = message;
    const [chat] = getSelectedChatConversation({
      allConversations,
      selectedChatId: conversationId,
    });
    if (!chat) return;

    const pendingMessageIndex = findPendingMessageIndex(chat, message);
    if (pendingMessageIndex !== -1) {
      Vue.set(chat.messages, pendingMessageIndex, message);
    } else {
      chat.messages.push(message);
      chat.timestamp = message.created_at;

      // Z-API Groups: Use local increment for unread count (per-agent tracking)
      // Backend can't reliably calculate per-agent unread in WebSocket context
      // The correct count is synced when ConversationFinder loads the list
      // Detection: prefer meta.channel when present, fallback to zapi_group_id
      // (same logic as MoreActions.vue for consistency)
      const metaChannel = chat.meta?.channel;
      const hasZapiGroupId = !!chat.additional_attributes?.zapi_group_id;
      const isZapiGroups = metaChannel
        ? metaChannel === 'Channel::ZapiGroups'
        : hasZapiGroupId;
      const isIncoming = message.message_type === 0;
      const isChatOpen = selectedChatId === conversationId;

      if (isZapiGroups) {
        if (isChatOpen && isIncoming) {
          // Agent is viewing - explicitly set to 0 to prevent stale counts
          chat.unread_count = 0;
        } else if (isIncoming && !isChatOpen) {
          // Increment locally when chat is not open
          chat.unread_count = (chat.unread_count || 0) + 1;
        }
      } else {
        // Standard channels: Use backend-provided count
        const { conversation: { unread_count: unreadCount = 0 } = {} } = message;
        chat.unread_count = unreadCount;
      }

      if (isChatOpen) {
        emitter.emit(BUS_EVENTS.FETCH_LABEL_SUGGESTIONS);
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
      }
    }
  },

  [types.ADD_CONVERSATION](_state, conversation) {
    _state.allConversations.push(conversation);
    invalidateConversationCache(_state);
  },

  [types.UPDATE_CONVERSATION](_state, conversation) {
    const { allConversations } = _state;
    const currentConversationIndex = allConversations.findIndex(
      c => c.id === conversation.id
    );
    if (currentConversationIndex > -1) {
      const { messages, ...conversationAttributes } = conversation;
      const existingConversation = allConversations[currentConversationIndex];
      const currentConversation = {
        ...existingConversation,
        ...conversationAttributes,
      };
      Vue.set(allConversations, currentConversationIndex, currentConversation);
      // Invalidate cache if sort-affecting fields OR filter-affecting fields changed
      // Sort: last_activity_at, priority, etc. (affects order)
      // Filter: assignee, team (affects which tab conversation appears in)
      // Compare with merged state (currentConversation), not the payload (conversationAttributes)
      if (
        shouldInvalidateSort(existingConversation, currentConversation) ||
        shouldInvalidateFilters(existingConversation, currentConversation)
      ) {
        invalidateConversationCache(_state);
      }
      if (_state.selectedChatId === conversation.id) {
        emitter.emit(BUS_EVENTS.FETCH_LABEL_SUGGESTIONS);
      }
    }
  },

  [types.REMOVE_CONVERSATION](_state, conversationId) {
    const index = _state.allConversations.findIndex(c => c.id === conversationId);
    if (index > -1) {
      _state.allConversations.splice(index, 1);
      invalidateConversationCache(_state);
    }
  },

  [types.SET_TAKE_OVER_CONVERSATIONS_IDS](_state, botAgentIds) {
    Vue.set(_state, 'takeOverConversationIds', botAgentIds);
  },

  [types.REMOVE_FROM_TAKE_OVER_CONVERSATIONS_IDS](_state, conversationId) {
    // Note: state key is takeOverConversationIds (without extra 's')
    _state.takeOverConversationIds = _state.takeOverConversationIds.filter(
      id => id !== conversationId
    );
  },

  [types.SET_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = true;
  },

  [types.CLEAR_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = false;
  },

  SET_CONVERSATION_FETCHING_STATUS(_state, status) {
    _state.isFetchingConversation = status;
  },

  [types.UPDATE_MESSAGE_UNREAD_COUNT](
    _state,
    { id, lastSeen, unreadCount = 0 }
  ) {
    const [chat] = _state.allConversations.filter(c => c.id === id);
    if (chat) {
      Vue.set(chat, 'agent_last_seen_at', lastSeen);
      Vue.set(chat, 'unread_count', unreadCount);
    }
  },
  [types.CHANGE_CHAT_STATUS_FILTER](_state, data) {
    _state.chatStatusFilter = data;
  },

  [types.CHANGE_CHAT_SORT_FILTER](_state, data) {
    _state.chatSortFilter = data;
    // Invalidate cache since sort filter change requires re-sorting
    invalidateConversationCache(_state);
  },

  // Update assignee on action cable message
  [types.UPDATE_ASSIGNEE](_state, payload) {
    const [chat] = _state.allConversations.filter(c => c.id === payload.id);
    if (chat) {
      Vue.set(chat.meta, 'assignee', payload.assignee);
      // Assignee change affects filtering (which tab conversation appears in)
      invalidateConversationCache(_state);
    }
  },

  [types.UPDATE_CONVERSATION_CONTACT](_state, { conversationId, ...payload }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (chat) {
      Vue.set(chat.meta, 'sender', payload);
    }
  },

  [types.SET_ACTIVE_INBOX](_state, inboxId) {
    _state.currentInbox = inboxId ? parseInt(inboxId, 10) : null;
  },

  [types.SET_CONVERSATION_CAN_REPLY](_state, { conversationId, canReply }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (chat) {
      Vue.set(chat, 'can_reply', canReply);
    }
  },

  [types.CLEAR_CONTACT_CONVERSATIONS](_state, contactId) {
    const chats = _state.allConversations.filter(
      c => c.meta.sender.id !== contactId
    );
    Vue.set(_state, 'allConversations', chats);
  },

  [types.SET_CONVERSATION_FILTERS](_state, data) {
    _state.appliedFilters = data;
  },

  [types.CLEAR_CONVERSATION_FILTERS](_state) {
    _state.appliedFilters = [];
  },

  [types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION](
    _state,
    { conversationId, messageId }
  ) {
    _state.syncConversationsMessages[conversationId] = messageId;
  },

  [types.SET_CONTEXT_MENU_CHAT_ID](_state, chatId) {
    _state.contextMenuChatId = chatId;
  },

  [types.SET_CHAT_LIST_FILTERS](_state, data) {
    _state.conversationFilters = data;
  },
  [types.UPDATE_CHAT_LIST_FILTERS](_state, data) {
    _state.conversationFilters = { ..._state.conversationFilters, ...data };
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};